// CareLogix™ ORACLE — Animated Funding Heatmap (Phase 33 §7)
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { PALETTE, scoreColor } from "@/components/animation/MotionShared";
import { fundingApi } from "@/api/phase33Client";

export default function FundingHeatmap({ bucket = "all", testid = "funding-heatmap" }) {
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fundingApi.opportunities({ bucket, limit: 32 })
      .then((r) => { if (!cancelled) setOpps(r.data?.opportunities || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [bucket]);

  const cells = useMemo(() => opps.map((o) => ({
    id: o.opportunity_id,
    source: o.source,
    priority: o.scores.priority_score,
    days: o.days_to_deadline,
  })), [opps]);

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div>
          <span style={S.eyebrow}>FUNDING HEATMAP™</span>
          <h3 style={S.title}>Sources × Priority · {opps.length} cells</h3>
        </div>
      </header>
      {loading && <div style={S.loading}>Loading…</div>}
      <div style={S.grid} data-testid={`${testid}-grid`}>
        {cells.map((c, i) => (
          <motion.div
            key={c.id}
            style={{ ...S.cell, background: scoreColor(c.priority), opacity: 0.10 + (c.priority / 100) * 0.85 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.10 + (c.priority / 100) * 0.85 }}
            transition={{ duration: 0.5, delay: i * 0.02 }}
            title={`${c.source} · priority ${c.priority} · ${c.days}d`}
            data-testid={`${testid}-cell-${c.id}`}
          >
            <span style={S.cellLbl}>{c.priority}</span>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

const S = {
  shell:   { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 14, padding: 12, color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 8 },
  head:    { display: "flex", justifyContent: "space-between", alignItems: "center" },
  eyebrow: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.20em" },
  title:   { margin: 0, color: PALETTE.goldBright, fontSize: 13 },
  loading: { color: PALETTE.slate4, fontSize: 12 },
  grid:    { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(56px, 1fr))", gap: 4 },
  cell:    { aspectRatio: "1 / 1", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(4,6,8,0.9)", fontFamily: "'Space Mono', monospace", fontWeight: 800, fontSize: 11 },
  cellLbl: { textShadow: "0 1px 2px rgba(0,0,0,0.55)" },
};
